import { Subscription } from 'rxjs';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Tag } from '@app/features/tag/shared/tag.type';
import { Validators, FormGroup, FormControl } from '@angular/forms';
import { TemplateService } from '@app/shared/services/template.service';
import { Template } from '@app/shared/models/template';
import { NgSelectSearchEvent } from '@app/shared/models/select-search-event';
import { Profile } from '@app/core/profile';
import { Role } from '@app/shared/models/role';
import { TemplateContextToken } from '@app/shared/tokens/template-context.token';
import { TemplateContext } from '@app/shared/models/template-context';
import { SearchUtilsService } from '@app/shared/services/search-utils.service';
import { TemplatePurpose } from '@app/shared/models/template-purpose';

type Mode = 'create' | 'edit' | 'view';
type ToolbarItem = string | { list: string };
interface QuillModules {
  toolbar: false | ToolbarItem[];
}

@Component({
  selector: 'omg-template-modal',
  templateUrl: './template-modal.component.html',
  styleUrls: ['./template-modal.component.scss'],
})
export class TemplateModalComponent implements OnInit {

  @Input() mode: Mode;
  @Input() template: Template;
  @Input() user: Profile;
  @Input() tags: Tag[];

  form: FormGroup<{
    name: FormControl<string>,
    body: FormControl<string>,
    selectedTags: FormControl<Tag[]>,
    purpose: FormControl<TemplatePurpose|null>
  }>;
  tagDropdownIsOpen = false;
  valueChangeSubscription: Subscription;
  autoLabsRadioOptionVisible: boolean;
  quillModules: QuillModules;
  quillFormat: string;

  constructor(
    @Inject(TemplateContextToken) public context: TemplateContext,
    public modal: NgbActiveModal,
    public templateService: TemplateService,
    public searchUtils: SearchUtilsService,
  ) {}

  ngOnInit(): void {
    this.initControls();
    this.initQuillModules();
  }

  async initControls(): Promise<void> {
    const templateEditor = this.user.roles.includes(Role.TemplateEditor);
    const automatedTemplateEditor = this.user.roles.includes(Role.AutomatedTemplateEditor);
    const purpose = this.templateService.purpose(this.template);
    const purposeEnabled = templateEditor || automatedTemplateEditor;
    const tags = await this.searchUtils.mapToTags(this.template.tags);
    this.autoLabsRadioOptionVisible = automatedTemplateEditor && this.context.type === 'message';

    this.form = new FormGroup({
      name: new FormControl(this.template.name, Validators.required),
      body: new FormControl(this.template.body, Validators.required),
      selectedTags: new FormControl(tags),
      purpose: new FormControl({
        value: purpose,
        disabled: !purposeEnabled,
      }),
    });
  }

  onSearch(event: NgSelectSearchEvent): void {
    this.tagDropdownIsOpen = event.term.length >= 2;
  }

  closeDropdown(): void {
    this.tagDropdownIsOpen = false;
  }

  doSave(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const { body, name, selectedTags } = this.form.value;
      const purpose = this.form.value.purpose ? this.form.value.purpose : 'personal';
      const internalUserId = purpose === 'personal' ? this.user.id : null;
      if (this.mode === 'edit') {
        this.context.actions.update({
          id: this.template.id,
          changes: { body, name, tags: selectedTags, internalUserId, purpose },
        });
      } else if (this.mode === 'create') {
        this.context.actions.create({ body, name, tags: selectedTags, internalUserId, purpose });
      }
      this.modal.close();
    }
  }

  private initQuillModules(): void {
    if (this.context.type == 'text') {
      this.quillFormat = 'text';
      this.quillModules = {
        toolbar: false
      };
    } else {
      this.quillFormat = 'html';
      this.quillModules = {
        toolbar: [
          'bold',
          { list: 'ordered' },
          { list: 'bullet' },
          'link'
        ],
      };
    }
  }
}
